import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[8%]" },
  { key: "biznesi", name: "Biznesi", sort_value: "name", className: "w-[36%]" },
  { key: "activity", name: "Aktiviteti", sort_value: "activity_name", className: "w-[28%]" },
  { key: "nac", name: "Kodi NAC", sort_value: "activity_code", className: "w-[12%]" },
  { key: "created", name: "Krijuar", sort_value: "created_at", className: "w-[16%]" },
];

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4">
      <div className="space-y-2">
        <div className="h-3 w-48 bg-gray-200 rounded" />
        <div className="h-2.5 w-32 bg-gray-100 rounded" />
      </div>
    </td>
    <td className="px-6 py-4"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-12 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-20 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function BusinessTable({
  businesses,
  isLoading,
  params = {},
  onSort,
  onRowClick,
}) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!businesses || businesses.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : businesses?.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => onRowClick?.(b)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                  >
                    <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                      #{b.id}
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">
                          {b.name || "—"}
                        </div>
                        <div className="text-xs text-gray-500 truncate tabular-nums">
                          {b.nui ? `NUI ${b.nui}` : "—"}
                          {b.fiscal_number ? ` · ${b.fiscal_number}` : ""}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-gray-700">
                      <span className="block truncate max-w-[260px]">
                        {b.activity_name || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-gray-700 tabular-nums whitespace-nowrap">
                      {b.activity_code || "—"}
                    </td>
                    <td className="px-6 py-3.5 text-gray-600 whitespace-nowrap tabular-nums text-xs">
                      {formatDate(b.created_at)}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!businesses || businesses.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          businesses?.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => onRowClick?.(b)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {b.name || "—"}
                  </div>
                  <div className="text-xs text-gray-500 tabular-nums">
                    #{b.id}
                    {b.nui ? ` · NUI ${b.nui}` : ""}
                  </div>
                </div>
              </div>
              {b.activity_name && (
                <div className="mt-1 text-xs text-gray-600 truncate">
                  {b.activity_code ? `${b.activity_code} · ` : ""}
                  {b.activity_name}
                </div>
              )}
              <div className="mt-2 flex items-center justify-end text-xs text-gray-500">
                <span className="tabular-nums">{formatDate(b.created_at)}</span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
